import { useLayoutEffect, useRef, type DependencyList } from "react";

/**
 * A sideways-scrolling box of week columns that opens at its NEWEST week, on the right.
 *
 * ⚠ Scrolling to the end once, on render, is not enough: the columns are measured in the
 * fallback font, Montserrat then arrives and widens them, and the box is left a few weeks
 * short of the newest — measured on the Cocon Requests tables, 26 Oct 2026. So it scrolls
 * again whenever the table's size changes, until the person scrolls it themselves.
 */
export function useOpensAtNewest<T extends HTMLElement>(deps: DependencyList) {
  const ref = useRef<T | null>(null);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    let moved = false;
    const toNewest = () => {
      if (!moved) box.scrollLeft = box.scrollWidth;
    };
    const byHand = () => {
      moved = true;
    };
    toNewest();
    const observer = new ResizeObserver(toNewest);
    if (box.firstElementChild) observer.observe(box.firstElementChild);
    void document.fonts?.ready.then(toNewest);
    // A programmatic scroll fires `scroll` too, so the person's intent is read from input.
    for (const type of ["wheel", "pointerdown", "touchstart", "keydown"] as const) {
      box.addEventListener(type, byHand, { passive: true });
    }
    return () => {
      observer.disconnect();
      for (const type of ["wheel", "pointerdown", "touchstart", "keydown"] as const) box.removeEventListener(type, byHand);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}
